import { Fragment, ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import {
  COMMENT_FILTER_ALL,
  DEFAULT_SHOW_IN_PLANS,
  DEFAULT_COMMENT_ORIENTATION,
  commentTypePresets,
  getCommentFilterCategory,
  getCommentType,
  getNewCommentTypeForCommentFilter,
  getCommentOrientation,
  isCommentVisibleForCommentFilter,
  isCommentVisibleForStoreys,
  toggleCommentOrientation,
} from '@constants'
import { DrawControllerWrapper, OrthoSnapIndicator } from '@scene'
import { Vector3 } from 'three'
import {
  ArrowOutward,
  ContentCopy,
  Delete,
  Edit,
  Height,
  Link as LinkIcon,
  LinkOff,
  OpenWith,
  TextRotationNone,
  TextRotationDown,
} from '@mui/icons-material'
import { getPlanViewStorey } from '@editor/pages/PlanView/utils'
import { getPdfLabelBoxHeightWorld } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/pdfLabelBox'
import { useModelSnapTargets } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/hooks'
import { TransientDrawState, useCameraStore, useTapelineSnapTargets } from '@modugen/ui-toolkit'
import {
  getSelectedPlanElement,
  useCommentFilterStore,
  useCommentsStore,
  useControlStore,
  useModelStore,
} from '@editorStores'
import {
  usePlanElementSelection,
  useClickActionEnabled,
  useGuidToElementIncludingRips,
  useLabelScaleFromPlanSettings,
  usePlanLabelPdfMetrics,
  useMouseMovedSinceMouseDown,
  useSelectedPlanElementId,
  useSelectionMode,
} from '@editorHooks'
import { CommentCreateDTO } from '@mutations'
import { ADD_COMMENTS_SELECTION_KEY } from '../../pages/Editor/components/AddCommentToolButton/useAddCommentTool'
import Comment3D from '../Comment3D'
import SceneHud, { SceneHudAction } from '../SceneHud'
import useCommentSnapTargets from '../hooks/useCommentSnapTargets'
import usePlanPositionSnapTargets from '../hooks/usePlanPositionSnapTargets'
import useScenePositionDrag, { ScenePosition } from '../hooks/useScenePositionDrag'
import CommentCalloutControls from './CommentCalloutControls'
import CommentEditPopup from './CommentEditPopup'
import CommentTextEditor from './CommentTextEditor'
import useCommentCallouts from './useCommentCallouts'
import useCommentEsc from './useCommentEsc'
import useCommentMutations from './useCommentMutations'
import { moveComment } from './utils'

// Picks the storey sitting on the storey boundary nearest to the given z, using each storey's floor
// (lower boundary) as its reference. A point just below a boundary snaps up to the storey above it
// (e.g. with floors 0/2/4, z=3.99 → storey 2, not storey 1); ties resolve to the lower storey. Used
// to default a newly placed 3D comment to a sensible storey (the vertical axis is z).
const getNearestStoreyByZ = (z: number, storeyBoundaries: StoreyBoundaries): string | undefined => {
  const storeysByFloor = Object.entries(storeyBoundaries)
    .map(([storey, [lower]]) => ({ storey, lower }))
    .sort((a, b) => a.lower - b.lower)

  let nearestStorey: string | undefined
  let minDistance = Infinity
  for (const { storey, lower } of storeysByFloor) {
    const distance = Math.abs(z - lower)
    if (distance < minDistance) {
      minDistance = distance
      nearestStorey = storey
    }
  }
  return nearestStorey
}

/**
 * In-scene comment controller. Replaces the old `Comments3D`: it renders the
 * (storey- and type-filtered) comments, handles dropping a new comment via the
 * `add-comments` selection mode, and shows move/edit controls for the selected
 * comment. Drag-to-move is delegated to the shared `useScenePositionDrag`;
 * callout editing lives in `useCommentCallouts`. Works in both 3D and
 * orthographic (floorplan) mode.
 */
const CommentsController = (): ReactElement => {
  const comments = useCommentsStore(state => state.comments)
  const selectedId = useSelectedPlanElementId('comment')
  const { selectPlanElement, clearPlanSelection } = usePlanElementSelection()

  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const model = useModelStore(state => state.model)

  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const setSelectedPlan = useCommentFilterStore(state => state.setSelectedPlan)

  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)

  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const labelScale = useLabelScaleFromPlanSettings()
  const pdfMetrics = usePlanLabelPdfMetrics()
  const clickActionEnabled = useClickActionEnabled()

  const guidToElement = useGuidToElementIncludingRips()

  const { selectionModeKey, setSelectionMode, unsetSelectionMode } = useSelectionMode()
  const { createComment, updateCommentFields, deleteComment } = useCommentMutations()

  // const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  // const [textEditingId, setTextEditingId] = useState<string | null>(null)

  const [editingState, setEditingState] = useState<'editing' | 'text-editing' | null>(null)

  // Id of a comment just created via the add tool. Deselecting it must not pull
  // the plan filter to its category (a freshly placed comment was placed, not
  // inspected) – only the first deselect is skipped.
  const justCreatedCommentIdRef = useRef<string | null>(null)

  const commitCommentPosition = useCallback(
    (id: string, position: ScenePosition) => {
      const comment = useCommentsStore.getState().comments.find(current => current.id === id)
      if (!comment) return
      // Only the position moves; callouts keep their (fixed) end/knee targets.
      updateCommentFields(id, { position: position as Point })
    },
    [updateCommentFields],
  )

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const { pointTargets: xySnapTargets, lineTargets: xyLineSnapTargets } =
    usePlanPositionSnapTargets(selectedId, {
      snapToCornersAndEdges,
      orthoSnap: snapOrthogonal,
    })

  const { startDrag, dragPreview, activeDragMode, isDraggingRef } = useScenePositionDrag({
    storeyBoundaries: model.storey_boundaries,
    setIsDrawingActive,
    onCommit: commitCommentPosition,
    xySnapTargets,
    xyLineSnapTargets,
  })

  const isAddMode = selectionModeKey === ADD_COMMENTS_SELECTION_KEY

  const visibleComments = useMemo(() => {
    return comments.filter(comment => {
      // The active (selected) comment is always shown, regardless of any filter.
      if (selectedId === comment.id) return true
      if (!isCommentVisibleForCommentFilter(comment, selectedPlan)) return false
      return isCommentVisibleForStoreys(comment, visibleStoreys)
    })
  }, [comments, selectedId, selectedPlan, visibleStoreys])

  const selectedComment = useMemo(
    () => (selectedId ? comments.find(comment => comment.id === selectedId) ?? null : null),
    [comments, selectedId],
  )

  const handleCommentClick = useCallback(
    (comment: Comment3D) => {
      // Clicking an already-selected comment starts in-place text editing;
      // otherwise the click just selects it. Linked comments are edited via the
      // "Kommentar Positionsliste" field, so in-scene text editing stays off.
      if (selectedId === comment.id) {
        if (comment.element_guid) return
        setEditingState('text-editing')
      } else {
        setEditingState(null)
        selectPlanElement('comment', comment.id)
      }
    },
    [selectedId, selectPlanElement],
  )

  const handlePick = useCallback(
    async (point: Vector3) => {
      const previousIds = new Set(comments.map(comment => comment.id))

      let storeyForSceneRendering: string | null = null
      // The plan storey: "Dach" while only the roof is shown on the top floorplan (plan view).
      let planStorey: string | null = null
      let z = point.z
      if (isOrthographic && floorplanStorey) {
        storeyForSceneRendering = floorplanStorey
        planStorey = getPlanViewStorey(floorplanStorey, visibleStoreys)
        const boundary = model.storey_boundaries[floorplanStorey]
        if (boundary) z = boundary[1]
      } else {
        // In 3D (perspective) mode there is no active floorplan storey — default the comment to the
        // storey sitting on the nearest storey boundary.
        const nearestStorey = getNearestStoreyByZ(point.z, model.storey_boundaries)
        if (nearestStorey) storeyForSceneRendering = nearestStorey
        planStorey = nearestStorey ?? null
      }

      // New comments take the type of the filtered plan (the default type under "Alle"), so they
      // stay visible under the filter; color and showInPlans are derived from the type preset.
      const type = getNewCommentTypeForCommentFilter(selectedPlan)
      const preset = commentTypePresets[type]
      const dto: CommentCreateDTO = {
        position: { x: point.x, y: point.y, z: z + 0.01 },
        text: 'Neuer Kommentar',
        type,
        orientation: DEFAULT_COMMENT_ORIENTATION,
        color: preset.color,
        backgroundColor: preset.backgroundColor,
        showTitle: true,
        storeysForPlans: planStorey ? [planStorey] : [],
        showInPlans: preset.showInPlans,
        storeyForSceneRendering,
      }

      const updated: Comment3D[] = await createComment(dto)
      const created = updated.find(comment => !previousIds.has(comment.id))
      if (created) {
        justCreatedCommentIdRef.current = created.id
        selectPlanElement('comment', created.id)
        // Drop straight into the in-place text editor so the user can type the
        // comment text right after placing it (the default text is selected).
        setEditingState('text-editing')
      }
      unsetSelectionMode()
    },
    [
      comments,
      isOrthographic,
      floorplanStorey,
      model,
      selectedPlan,
      visibleStoreys,
      createComment,
      selectPlanElement,
      unsetSelectionMode,
    ],
  )

  const isSelectedVisible =
    !!selectedComment && visibleComments.some(comment => comment.id === selectedComment.id)

  const displayedSelectedComment =
    !isSelectedVisible || !selectedComment
      ? null
      : dragPreview && dragPreview.id === selectedComment.id
      ? moveComment(selectedComment, dragPreview.position as Point)
      : selectedComment

  // Anchor the HUD at the bottom-center of the label box (in world units) so the
  // buttons track the box's actual bottom edge at every zoom level and stay
  // outside it, instead of sitting at a fixed screen offset from the center.
  const hudPosition = useMemo(() => {
    if (!displayedSelectedComment) return null
    const { x, y, z } = displayedSelectedComment.position
    const numLines = displayedSelectedComment.showTitle
      ? displayedSelectedComment.text.split('\n').length
      : 0
    const boxHeight = getPdfLabelBoxHeightWorld(pdfMetrics, true, numLines)
    return new Vector3(x, y - boxHeight / 2, z)
  }, [displayedSelectedComment, pdfMetrics])

  const handleDelete = useCallback(
    (id: string) => {
      setEditingState(null)
      clearPlanSelection()
      deleteComment(id)
    },
    [deleteComment, clearPlanSelection],
  )

  // Detach a comment from its element (element_guid → null). The comment itself
  // stays; afterwards it is a normal standalone comment (editable, deletable).
  const handleUnlink = useCallback(
    (id: string) => {
      updateCommentFields(id, { element_guid: null })
    },
    [updateCommentFields],
  )

  const handleCopy = useCallback(
    async (source: Comment3D) => {
      const previousIds = new Set(comments.map(comment => comment.id))
      // Offset the copy so it doesn't sit exactly on top of the original, and
      // shift any callout points by the same delta so the arrows stay attached.
      const dx = 0.25
      const dy = -0.25

      const dto: CommentCreateDTO = {
        position: { x: source.position.x + dx, y: source.position.y + dy, z: source.position.z },
        text: source.text,
        type: getCommentType(source),
        orientation: getCommentOrientation(source),
        color: source.color,
        backgroundColor: source.backgroundColor,
        showTitle: source.showTitle,
        storeysForPlans: source.storeysForPlans ?? [],
        showInPlans: source.showInPlans ?? DEFAULT_SHOW_IN_PLANS,
        callouts:
          source.callouts?.map(callout => ({
            end: { x: callout.end.x + dx, y: callout.end.y + dy, z: callout.end.z },
            knee: callout.knee
              ? { x: callout.knee.x + dx, y: callout.knee.y + dy, z: callout.knee.z }
              : null,
          })) ?? null,
        storeyForSceneRendering: source.storeyForSceneRendering ?? null,
      }

      const updated: Comment3D[] = await createComment(dto)
      const created = updated.find(comment => !previousIds.has(comment.id))
      if (created) selectPlanElement('comment', created.id)
    },
    [comments, createComment, selectPlanElement],
  )

  const handleToggleOrientation = useCallback(
    (comment: Comment3D) => {
      updateCommentFields(comment.id, {
        orientation: toggleCommentOrientation(getCommentOrientation(comment)),
      })
    },
    [updateCommentFields],
  )

  const {
    isCalloutMode,
    isKneeMode,
    calloutSnapTargets,
    startCallout,
    handleAddCalloutPoint,
    handleAddKneePoint,
    selectedCalloutIndex,
    setSelectedCalloutIndex,
    hoveredCalloutIndex,
    handleHoverCallout,
    handleDeleteCallout,
    startKnee,
    handleRemoveKnee,
    cancelDrawing,
  } = useCommentCallouts({
    comments,
    activeComment: displayedSelectedComment,
    isOrthographic,
    selectionModeKey,
    updateCommentFields,
    setSelectionMode,
    unsetSelectionMode,
  })

  // On deselect, switch the filter to the comment's own category so it stays
  // visible after losing its always-shown (selected) status. Plan-less comments
  // (Notiz / plan-less custom) map to the Notiz category.
  const applyDeselectedCommentPlan = useCallback(
    (id: string) => {
      // Under "Alle" the comment stays visible after deselect, so don't change the filter.
      if (useCommentFilterStore.getState().selectedPlan === COMMENT_FILTER_ALL) return
      // Don't hijack the filter when deselecting a just-added comment.
      if (justCreatedCommentIdRef.current === id) {
        justCreatedCommentIdRef.current = null
        return
      }
      const comment = useCommentsStore.getState().comments.find(current => current.id === id)
      if (!comment) return
      setSelectedPlan(getCommentFilterCategory(comment))
    },
    [setSelectedPlan],
  )

  // Clicking the scene away from a comment deselects it. R3F reports a click as
  // a scene "miss" even when it lands on the HUD or happens mid-drag, so we skip
  // those: a HUD press (hudInteractionRef) and a click while dragging
  // (isDraggingRef) must both keep the comment selected. Any other click away
  // from it — empty space, a model element, another plan element — deselects it.
  // The selection is only cleared if this comment is still the selected one.
  const deselectComment = useCallback(
    (id: string) => {
      if (isDraggingRef.current) return
      applyDeselectedCommentPlan(id)
      setEditingState(null)
      const selectedPlanElement = getSelectedPlanElement()
      if (selectedPlanElement?.type === 'comment' && selectedPlanElement.id === id) {
        clearPlanSelection()
      }
    },
    [isDraggingRef, applyDeselectedCommentPlan, clearPlanSelection],
  )

  // Esc deselects the active comment (or cancels callout drawing). While editing
  // the text or the edit popup is open, those components handle Escape
  // themselves, so we stay out of the way.
  const handleEsc = useCallback(() => {
    if (isCalloutMode || isKneeMode) {
      cancelDrawing()
      return
    }
    if (selectedCalloutIndex !== null) {
      setSelectedCalloutIndex(null)
      return
    }
    if (editingState) return
    if (selectedId) {
      applyDeselectedCommentPlan(selectedId)
      clearPlanSelection()
    }
  }, [
    isCalloutMode,
    isKneeMode,
    cancelDrawing,
    selectedCalloutIndex,
    setSelectedCalloutIndex,
    editingState,
    selectedId,
    applyDeselectedCommentPlan,
    clearPlanSelection,
  ])

  // Activate the comment Esc scope whenever there is a comment action to escape,
  // so it is the only Escape handler that runs (other scopes defer to it).
  const escEnabled = isCalloutMode || isKneeMode || selectedCalloutIndex !== null || !!selectedId
  useCommentEsc(handleEsc, escEnabled)

  // Show the interactive callout overlay (hover/select/delete) for the active
  // comment, except while adding a comment/callout or editing text.
  const showCalloutControls =
    !isAddMode && !isCalloutMode && !isKneeMode && !editingState && !!displayedSelectedComment

  // HUD buttons for the active comment (move / edit / callout, then copy /
  // delete). Empty when nothing is selected; the HUD itself is only rendered
  // when there is a comment and a position.
  const hudGroups = useMemo<SceneHudAction[][]>(() => {
    if (!displayedSelectedComment) return []

    const linkedGuid = displayedSelectedComment.element_guid
    const isLinked = !!linkedGuid
    const linkedElementExists = isLinked && !!guidToElement[linkedGuid]
    const isVertical = getCommentOrientation(displayedSelectedComment) === 'vertical'

    const moveActions: SceneHudAction[] = [
      {
        key: 'move-xy',
        tooltip: 'In der Ebene verschieben',
        icon: <OpenWith fontSize="small" />,
        onPointerDown: event =>
          startDrag('xy', displayedSelectedComment.id, displayedSelectedComment.position, event),
        active: activeDragMode === 'xy',
        dataCy: 'comment-move-xy',
      },
    ]
    if (!isOrthographic) {
      moveActions.push({
        key: 'move-z',
        tooltip: 'Vertikal verschieben (z)',
        icon: <Height fontSize="small" />,
        onPointerDown: event =>
          startDrag('z', displayedSelectedComment.id, displayedSelectedComment.position, event),
        active: activeDragMode === 'z',
        dataCy: 'comment-move-vertical',
      })
    }

    return [
      [
        ...moveActions,
        {
          key: 'edit',
          // Linked comments open the same popup, but their text/type are locked there (owned by
          // the "Kommentar Positionsliste" field); the popup offers a "jump to element" action.
          tooltip: isLinked
            ? 'Kommentar bearbeiten (Text & Typ über die Positionsliste)'
            : 'Kommentar bearbeiten',
          icon: <Edit fontSize="small" />,
          onClick: () => setEditingState('editing'),
          active: editingState === 'editing',
          dataCy: 'comment-edit',
        },
        {
          key: 'add-callout',
          tooltip: 'Verweispfeil hinzufügen',
          icon: <ArrowOutward fontSize="small" />,
          onClick: () => startCallout(displayedSelectedComment),
          active: isCalloutMode,
          dataCy: 'comment-add-callout',
        },
        {
          key: 'toggle-orientation',
          tooltip: isVertical ? 'Horizontal ausrichten' : 'Vertikal ausrichten',
          icon: isVertical ? (
            <TextRotationNone fontSize="small" />
          ) : (
            <TextRotationDown fontSize="small" />
          ),
          onClick: () => handleToggleOrientation(displayedSelectedComment),
          dataCy: 'comment-toggle-orientation',
        },
      ],
      [
        {
          key: 'copy',
          tooltip: 'Kommentar kopieren',
          icon: <ContentCopy fontSize="small" />,
          onClick: () => handleCopy(displayedSelectedComment),
          dataCy: 'comment-copy',
        },
        // Unlink is only offered for linked comments. When the referenced element
        // no longer exists we show a warning (LinkOff) icon and say so.
        ...(isLinked
          ? [
              {
                key: 'unlink',
                tooltip: linkedElementExists
                  ? 'Verknüpfung mit Element aufheben'
                  : 'Referenziertes Element existiert nicht mehr – klicken, um die Verknüpfung aufzuheben',
                icon: linkedElementExists ? (
                  <LinkIcon fontSize="small" />
                ) : (
                  <LinkOff fontSize="small" color="warning" />
                ),
                onClick: () => handleUnlink(displayedSelectedComment.id),
                dataCy: 'comment-unlink',
              } as SceneHudAction,
            ]
          : []),
        {
          key: 'delete',
          // While linked, deletion is blocked; the user must unlink first.
          tooltip: isLinked
            ? 'Kann nicht gelöscht werden, solange mit einem Element verknüpft'
            : 'Kommentar löschen',
          icon: <Delete fontSize="small" />,
          onClick: () => handleDelete(displayedSelectedComment.id),
          variant: 'danger',
          disabled: isLinked,
          dataCy: 'comment-delete',
        },
      ],
    ]
  }, [
    displayedSelectedComment,
    guidToElement,
    activeDragMode,
    isOrthographic,
    editingState,
    isCalloutMode,
    startDrag,
    startCallout,
    handleCopy,
    handleToggleOrientation,
    handleUnlink,
    handleDelete,
  ])

  const { mouseMoved } = useMouseMovedSinceMouseDown()

  const snapToAngles = useControlStore(state => state.snapToAngles)

  const modelSnapTargets = useModelSnapTargets({ xyOnly: isOrthographic })
  const tapelineSnapTargets = useTapelineSnapTargets()
  const commentSnapTargets = useCommentSnapTargets(isOrthographic)

  const additionalSnapTargets = useMemo(() => {
    return [
      ...modelSnapTargets,
      ...tapelineSnapTargets,
      ...commentSnapTargets,
      ...(calloutSnapTargets ?? []),
    ]
  }, [modelSnapTargets, tapelineSnapTargets, commentSnapTargets, calloutSnapTargets])

  return (
    <>
      {visibleComments.map(comment => {
        // The comment open in the edit popup is rendered there as a live preview.
        if (editingState === 'editing' && comment.id === selectedId) return null
        const base =
          dragPreview && dragPreview.id === comment.id
            ? moveComment(comment, dragPreview.position as Point)
            : comment
        // Only the selected comment is text-edited; without the id check every
        // visible comment would mount its own editor and they'd blur-commit
        // each other away immediately.
        const isEditingText = editingState === 'text-editing' && comment.id === selectedId
        // Hide the 3D label while editing – the HTML textarea takes its place.
        const display = isEditingText ? { ...base, showTitle: false } : base
        return (
          <Fragment key={comment.id}>
            <Comment3D
              comment={display}
              labelScale={labelScale}
              onClick={isAddMode || isCalloutMode || isKneeMode ? undefined : handleCommentClick}
              // The active comment's label is clickable to edit, so show the text
              // cursor on hover. Linked comments can only be edited via the
              // position list, so show a 'not-allowed' cursor instead. Suppressed
              // while drawing (clicks are disabled).
              hoverCursor={
                !isAddMode && !isCalloutMode && !isKneeMode && selectedId === comment.id
                  ? comment.element_guid
                    ? 'not-allowed'
                    : 'text'
                  : undefined
              }
              hideCallouts={showCalloutControls && displayedSelectedComment?.id === comment.id}
              onPointerMissed={
                displayedSelectedComment?.id === comment.id
                  ? (event: MouseEvent) => {
                      // Only a left click deselects; a right-button camera drag keeps it selected.
                      if (event.button !== 0) return
                      if (
                        mouseMoved.current ||
                        hoveredCalloutIndex !== null ||
                        isCalloutMode ||
                        isKneeMode
                      )
                        return

                      // A click on a HUD button (edit/copy/…) is a drei <Html>
                      // portal click that R3F still reports as a scene "miss".
                      // closest() walks all ancestors, so it matches whether the
                      // hit element is the button, its <svg>, or an inner <path>
                      // – unlike checking only the immediate parent.
                      const target = event.target
                      if (target instanceof Element && target.closest('.scene-hud-button')) return

                      deselectComment(comment.id)
                    }
                  : undefined
              }
            />
            {isEditingText && (
              <CommentTextEditor
                comment={base}
                labelScale={labelScale}
                onCommit={text => {
                  // Don't persist an empty/whitespace-only label — keep the prior text.
                  if (text.trim().length > 0) updateCommentFields(comment.id, { text })
                  setEditingState(null)
                }}
                onCancel={() => setEditingState(null)}
              />
            )}
          </Fragment>
        )
      })}

      {showCalloutControls && displayedSelectedComment && (
        <CommentCalloutControls
          comment={displayedSelectedComment}
          selectedIndex={selectedCalloutIndex}
          hoveredIndex={hoveredCalloutIndex}
          onHover={handleHoverCallout}
          onSelect={setSelectedCalloutIndex}
          onDelete={handleDeleteCallout}
          onAddKnee={startKnee}
          onRemoveKnee={handleRemoveKnee}
        />
      )}

      {!isAddMode && !editingState && displayedSelectedComment && hudPosition && (
        <SceneHud position={hudPosition} groups={hudGroups} />
      )}

      {selectedComment && editingState === 'editing' && (
        <CommentEditPopup
          comment={selectedComment}
          labelScale={labelScale}
          onClose={() => setEditingState(null)}
        />
      )}

      {isAddMode && (
        <DrawControllerWrapper
          enabled={clickActionEnabled}
          enableIndicator
          xyOnly={isOrthographic}
          onDrawEnd={(state: TransientDrawState) => {
            if (state.drawPoint) handlePick(state.drawPoint.v)
          }}
          additionalSnapTargets={additionalSnapTargets}
          snapToCornersAndEdges={snapToCornersAndEdges}
          snapToAngles={snapToAngles}
          orthoSnap={snapOrthogonal}
        />
      )}

      {isCalloutMode && (
        <DrawControllerWrapper
          enabled={clickActionEnabled}
          enableIndicator
          xyOnly={isOrthographic}
          additionalSnapTargets={additionalSnapTargets}
          snapToCornersAndEdges={snapToCornersAndEdges}
          snapToAngles={snapToAngles}
          orthoSnap={snapOrthogonal}
          onDrawEnd={(state: TransientDrawState) => {
            if (state.drawPoint) handleAddCalloutPoint(state.drawPoint.v)
          }}
        />
      )}

      {isKneeMode && (
        <DrawControllerWrapper
          enabled={clickActionEnabled}
          enableIndicator
          xyOnly={isOrthographic}
          additionalSnapTargets={additionalSnapTargets}
          snapToCornersAndEdges={snapToCornersAndEdges}
          snapToAngles={snapToAngles}
          orthoSnap={snapOrthogonal}
          onDrawEnd={(state: TransientDrawState) => {
            if (state.drawPoint) handleAddKneePoint(state.drawPoint.v)
          }}
        />
      )}

      {dragPreview?.isOrthoSnap && <OrthoSnapIndicator point={dragPreview.position} />}
    </>
  )
}

export default CommentsController
